import { Box, Link, Select, styled } from '@mui/material';

export const StyledHeader = styled(Box)(({ theme }) => ({
  height: '72px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1.5px solid transparent',
  background: 'transparent',
  fontFamily: 'Roboto, sans-serif',
  padding: '0 40px',
  position: 'relative',
  zIndex: 10,

  [theme.breakpoints.down('md')]: {
    height: '72px',
    padding: '0 8px',
  },
}));

export const StyledLogo = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  textDecoration: 'none',
  color: theme.palette.black.main,
  cursor: 'pointer',
  paddingLeft: 0,
  marginLeft: '64px',

  img: {
    height: '56px',
    width: '104px',
    borderRadius: '7px',
    objectFit: 'contain',
  },

  div: {
    fontSize: '24px',
    lineHeight: '28px',
    fontWeight: 700,

    span: {
      color: theme.palette.primary.main,
    },
  },

  [theme.breakpoints.down('sm')]: {
    marginLeft: '8px',
    img: {
      height: '40px',
      width: '72px',
    },
  },
}));

export const ControlNotLogin = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: 'auto',
  zIndex: theme.zIndex.appBar + 20,

  '.MuiSelect-select': {
    width: '63px',
    height: '32px',
  },

  '.MuiButton-root': {
    padding: '0px',
    borderColor: 'transparent',
    '&:active': {
      borderColor: 'transparent',
      color: theme.palette.blue.main,
    },
    '&:hover': {
      borderColor: 'transparent',
      color: theme.palette.blue.main,
    },
  },

  '.MuiButton-contained': {
    minWidth: '108px',
    fontSize: '14px',
    lineHeight: '16px',
    fontWeight: 500,
    textTransform: 'none',
  },

  [theme.breakpoints.down('sm')]: {
    '.MuiButton-contained': {
      minWidth: 'auto',
      width: '80px',
      height: '32px',
      fontSize: '12px',
      lineHeight: '14px',
      padding: '0px',
    },
  },
}));

/**
 * StyledSelectHeader
 * Custom styled MUI Select for language/currency/etc. in the header.
 * Includes popup (Menu) and MenuItem styling via defaultProps.
 */
export const StyledSelectHeader = styled(Select)(({ theme }) => ({
  // Base select styles
  display: 'flex',
  width: '100px',
  height: '42px',
  fontSize: '14px',
  lineHeight: '16px',
  fontWeight: 400,
  outline: 'none',
  background: theme.palette.blue[10],
  color: theme.palette.white.main,
  borderBottom: 'unset',
  borderRadius: '0.25rem',

  // Hover state
  '&:hover': {
    background: `${theme.palette.blue[10]}BF`,
  },

  // Icon color
  '.MuiSvgIcon-root': {
    color: theme.palette.white.main,
  },

  // Select display (button area)
  '.MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    minHeight: 'auto !important',
    textOverflow: 'unset !important',
  },

  // Remove border
  fieldset: {
    border: 'none',
  },

  // Responsive styles
  [theme.breakpoints.down('sm')]: {
    width: '58px',
    height: '28px',
    fontSize: '12px',
    lineHeight: '14px',
    fontWeight: 400,
  },
}));

// Default popup (Menu) and MenuItem styles for StyledSelectHeader
StyledSelectHeader.defaultProps = {
  MenuProps: {
    PaperProps: {
      sx: {
        background: theme => theme.palette.white.main,
        color: theme => theme.palette.black.main,
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
        mt: 1,
      },
    },
    MenuListProps: {
      sx: {
        py: '8px',
        '.MuiMenuItem-root': {
          fontSize: '16px',
          fontWeight: 400,
          color: theme => theme.palette.blue.main,
          px: 2,
          my: 0.5,
          gap: '8px', // Consistent with select display
          display: 'flex',
          alignItems: 'center',
          transition: 'background 0.2s',
          '&.Mui-selected, &:hover': {
            background: theme => theme.palette.blue[10],
            color: theme => theme.palette.white.main,
          },
        },
      },
    },
  },
};

export const FlagBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '20px',
  height: '20px',
  overflow: 'hidden',
  paddingTop: '3px',
  [theme.breakpoints.down('xs')]: {
    display: 'none',
  },
}));

export const ControlLogin = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: 'auto',
  marginLeft: '8px',
  position: 'relative',
  zIndex: theme.zIndex.appBar + 20,
  [theme.breakpoints.down('sm')]: {
    gap: '16px',
    marginLeft: '16px',
  },
}));

export const BookingTabs = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '18px',
  position: 'static',
  marginLeft: '28px',
  height: 'auto',
  flex: 1,
  justifyContent: 'center',
  paddingRight: '0',

  [theme.breakpoints.down('md')]: {
    gap: '0',
    position: 'static',
    left: 'unset',
    transform: 'unset',
    top: 'unset',
    height: 'unset',
  },
}));

export const BookingTabItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '16px',
  textDecoration: 'none',
  cursor: 'pointer',
  color: theme.palette.white.main,
  textAlign: 'center',
  textTransform: 'uppercase',
  padding: '10px 32px',
  borderRadius: '8px',
  background: 'transparent',
  transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
  letterSpacing: '1px',
  margin: '0 4px',

  '@media (min-width: 465px)': {
    fontSize: '18px',
    fontWeight: 700,
    lineHeight: '22px',
  },

  '&.active': {
    color: theme.palette.blue.main,
    background: theme.palette.white.main,
    boxShadow: '0 2px 8px rgba(224,160,64,0.10)',
    div: {
      width: '60%',
      left: '20%',
    },
  },

  '&:hover': {
    opacity: 1,
    color: theme.palette.blue.main,
    background: 'rgba(255,255,255,0.75)',
    div: {
      borderBottom: `4px solid ${theme.palette.primary.main}`,
      borderRadius: '2px',
      width: '60%',
      left: '20%',
    },
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    padding: '4px 8px',
    borderRadius: '8px',
  },
}));

export const CompactBookingTabs = styled(BookingTabs)(({ theme }) => ({
  flexWrap: 'nowrap',
  gap: '4px',
  [theme.breakpoints.down('md')]: {
    gap: '2px',
    marginLeft: '8px',
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
}));

export const CompactBookingTabItem = styled(BookingTabItem)(({ theme }) => ({
  fontSize: '11px',
  lineHeight: '14px',
  padding: '8px 10px',
  margin: 0,
  textTransform: 'none',
  letterSpacing: '0.2px',
  background: 'transparent',
  color: theme.palette.white.main,
  borderRadius: '6px',
  '&.active': {
    color: theme.palette.white.main,
    background: 'rgba(255,255,255,0.12)',
    boxShadow: 'none',
  },
  '&:hover': {
    color: theme.palette.white.main,
    background: 'rgba(255,255,255,0.1)',
    opacity: 1,
  },
  '&:hover div, &.active div': {
    borderBottom: 'none',
    width: 'auto',
    left: 'unset',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    lineHeight: '14px',
    padding: '8px 12px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '15px',
    lineHeight: '16px',
    padding: '8px 14px',
  },
}));

export const HeaderMenuWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  marginRight: '0',
  zIndex: theme.zIndex.appBar + 11,
  [theme.breakpoints.down('sm')]: {
    marginRight: '0',
  },
}));

export const HeaderMenuTriggerTab = styled(CompactBookingTabItem)(({ theme }) => ({
  padding: '8px 10px',
  margin: 0,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    padding: '6px 8px',
  },
}));

export const HeaderMenuDropdown = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 'auto',
  top: 'calc(100% + 8px)',
  minWidth: '220px',
  maxWidth: '320px',
  borderRadius: '10px',
  background: theme.palette.white.main,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.14)',
  border: '1px solid rgba(13, 110, 253, 0.18)',
  opacity: 0,
  transform: 'translateY(8px)',
  pointerEvents: 'none',
  transition: 'opacity 0.22s ease, transform 0.22s ease',
  overflow: 'hidden',
  zIndex: theme.zIndex.appBar + 12,
  '&.open': {
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
}));

export const HeaderMenuDropdownItem = styled(Box)(({ theme }) => ({
  padding: '12px 14px',
  color: theme.palette.blue.main,
  fontSize: '14px',
  lineHeight: '18px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.18s ease',
  '&:hover': {
    background: theme.palette.blue[10],
    color: theme.palette.white.main,
  },
  '&.active': {
    background: `${theme.palette.blue[10]}E6`,
    color: theme.palette.white.main,
  },
}));

export const HeaderMenuLabelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  whiteSpace: 'nowrap',
});

export const HeaderMenuArrow = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.24s cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'rotate(0deg)',
  '&.open': {
    transform: 'rotate(180deg)',
  },
});

export const HeaderMenuLoadingBox = styled(Box)({
  minHeight: '84px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const BookingTabItemUnderline = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  bottom: 2,
  width: '100%',
  height: '4px',
  background: 'transparent',
  borderRadius: '2px',
  transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',

  [theme.breakpoints.down('md')]: {
    bottom: 2,
  },

  [theme.breakpoints.down('sm')]: {
    bottom: 2,
  },
}));

export const StyledLoginButtonBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.blue[10],
  color: theme.palette.white.main,
  borderRadius: '4px',
  height: '42px',
  padding: '0 20px',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '16px',
  cursor: 'pointer',
  transition: 'background 0.2s cubic-bezier(.4,0,.2,1)',
  boxShadow: 'none',
  border: 'none',
  textTransform: 'uppercase',
  gap: '8px',

  '&:hover': {
    background: `${theme.palette.blue[10]}BF`,
    color: theme.palette.white.main,
  },

  [theme.breakpoints.down('sm')]: {
    height: '32px',
    padding: '0 10px',
    fontSize: '12px',
    lineHeight: '14px',
  },
}));

export const HeaderActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginRight: '10px',
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export const HeaderActionButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  height: '42px',
  padding: '0 16px',
  borderRadius: '8px',
  fontSize: '17px',
  lineHeight: '20px',
  fontWeight: 500,
  color: theme.palette.white.main,
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'opacity 0.2s ease',
  '&.tour': {
    background: '#D58429',
  },
  '&.etaxi': {
    background: '#2F66C1',
  },
  '&:hover': {
    opacity: 0.9,
  },
  [theme.breakpoints.down('md')]: {
    height: '34px',
    padding: '0 10px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '14px',
    gap: '5px',
  },
}));
